class Animations {
    constructor() {
        this.settings = {
            selectors: {
                headings: "h1, section .title--decor:not(.title--decor--sm)",
                excluded:
                    "#header, #footer, .site-header, .site-footer, body > header, body > footer, nav, [role='dialog'], [data-no-animations]",
                cards: ".services-card, .advantages-card",
                images: "section .image-wrap img, section .banner-image img, main .image-wrap img",
                ignoredImages: ".svg-icon, .logo, [class*='icon']",
            },
            observer: {
                threshold: 0.12,
                rootMargin: "0px 0px -16% 0px",
            },
            timing: {
                headingDuration: 750,
                elementDuration: 650,
                imageDuration: 850,
                headingWordStagger: 75,
                itemStagger: 70,
                maxItemStagger: 350,
                headingOffset: "0.35em",
                itemOffset: "12px",
                imageOffset: "6px",
                imageScale: 1.02,
                easing: "cubic-bezier(0.2, 0.65, 0.3, 1)",
            },
        };
        this.observer = null;
        this.mutationObserver = null;
        this.resizeObserver = null;
        this.resizeFrame = null;
    }

    init() {
        if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        this.start();
    }

    start() {
        const { selectors, timing, observer } = this.settings;
        const headings = [...document.querySelectorAll(selectors.headings)].filter(
            (heading) => !heading.closest(selectors.excluded),
        );
        const images = [...document.querySelectorAll(selectors.images)].filter(
            (image) =>
                !image.closest(`${selectors.excluded}, ${selectors.ignoredImages}`) &&
                !image.closest(selectors.cards) &&
                image.getAttribute("alt") !== "",
        );
        const cards = [...document.querySelectorAll(selectors.cards)].filter(
            (card) => !card.closest(selectors.excluded),
        );
        const animatedElements = new Set();

        headings.forEach((heading) => {
            this.splitHeading(heading);
            heading.classList.add("js-animate-heading");
            animatedElements.add(heading);
        });

        images.forEach((image) => {
            image.classList.add("js-animate-image");
            animatedElements.add(image);
        });

        cards.forEach((card) => {
            this.animateCard(card, timing);
            animatedElements.add(card);
        });

        if (!animatedElements.size) {
            return;
        }

        this.applySettings();
        document.documentElement.classList.add("has-scroll-animations");
        this.observer = new IntersectionObserver(this.handleIntersection, observer);
        animatedElements.forEach((element) => this.observer.observe(element));

        if ("ResizeObserver" in window) {
            this.resizeObserver = new ResizeObserver(this.scheduleHeadingLayout);
            headings.forEach((heading) => this.resizeObserver.observe(heading));
        } else {
            window.addEventListener("resize", this.scheduleHeadingLayout, { passive: true });
        }

        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        this.observeAddedContent(node);
                    }
                });
            });
        });
        this.mutationObserver.observe(document.body, { childList: true, subtree: true });

        this.updateHeadingLayout(headings);
        document.fonts?.ready.then(() => this.updateHeadingLayout(headings));
    }

    applySettings() {
        const { timing } = this.settings;
        const root = document.documentElement.style;

        root.setProperty("--animation-heading-duration", `${timing.headingDuration}ms`);
        root.setProperty("--animation-element-duration", `${timing.elementDuration}ms`);
        root.setProperty("--animation-image-duration", `${timing.imageDuration}ms`);
        root.setProperty("--animation-heading-offset", timing.headingOffset);
        root.setProperty("--animation-item-offset", timing.itemOffset);
        root.setProperty("--animation-image-offset", timing.imageOffset);
        root.setProperty("--animation-image-scale", timing.imageScale);
        root.setProperty("--animation-easing", timing.easing);
    }

    observeAddedContent(root) {
        const { selectors, timing } = this.settings;
        const headings = this.getElements(root, selectors.headings).filter(
            (heading) =>
                !heading.classList.contains("js-animate-heading") && !heading.closest(selectors.excluded),
        );
        const images = this.getElements(root, selectors.images).filter(
            (image) =>
                !image.classList.contains("js-animate-image") &&
                !image.closest(`${selectors.excluded}, ${selectors.ignoredImages}`) &&
                !image.closest(selectors.cards) &&
                image.getAttribute("alt") !== "",
        );
        const cards = this.getElements(root, selectors.cards).filter(
            (card) => !card.closest(selectors.excluded),
        );

        headings.forEach((heading) => {
            this.splitHeading(heading);
            heading.classList.add("js-animate-heading");
            this.observer.observe(heading);
            this.resizeObserver?.observe(heading);
        });

        images.forEach((image) => {
            image.classList.add("js-animate-image");
            this.observer.observe(image);
        });

        cards.forEach((card) => {
            if (card.classList.contains("js-animate-item")) {
                return;
            }

            this.animateCard(card, timing);
            this.observer.observe(card);
        });

        this.updateHeadingLayout(headings);
        document.fonts?.ready.then(() => this.updateHeadingLayout(headings));
    }

    getElements(root, selector) {
        const elements = root.matches?.(selector) ? [root] : [];
        return [...elements, ...root.querySelectorAll(selector)];
    }

    animateCard(card, timing) {
        const siblings = [...card.parentElement.children].filter((item) => item.matches(this.settings.selectors.cards));
        const index = siblings.indexOf(card);

        card.classList.add("js-animate-item");
        card.style.setProperty(
            "--item-delay",
            `${Math.min(index * timing.itemStagger, timing.maxItemStagger)}ms`,
        );
    }

    splitHeading(heading) {
        const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) =>
                node.textContent.trim() && !node.parentElement.closest("script, style, .js-animate-word")
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT,
        });
        const textNodes = [];

        while (walker.nextNode()) {
            textNodes.push(walker.currentNode);
        }

        textNodes.forEach((node) => {
            const fragment = document.createDocumentFragment();
            node.textContent.split(/(\s+)/u).forEach((part) => {
                if (!part) {
                    return;
                }

                if (/^\s+$/u.test(part)) {
                    fragment.append(document.createTextNode(part));
                    return;
                }

                const word = document.createElement("span");
                word.className = "js-animate-word";
                word.textContent = part;
                fragment.append(word);
            });
            node.replaceWith(fragment);
        });
    }

    handleIntersection = (entries, observer) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            entry.target.classList.add("is-animated");
            observer.unobserve(entry.target);
        });
    };

    scheduleHeadingLayout = () => {
        if (this.resizeFrame) {
            cancelAnimationFrame(this.resizeFrame);
        }

        this.resizeFrame = requestAnimationFrame(() => {
            this.updateHeadingLayout(document.querySelectorAll(".js-animate-heading"));
        });
    };

    updateHeadingLayout(headings) {
        headings.forEach((heading) => {
            if (heading.classList.contains("is-animated")) {
                return;
            }

            const words = [...heading.querySelectorAll(".js-animate-word")].map((word) => ({
                word,
                rect: word.getBoundingClientRect(),
            }));
            const rows = [];

            words
                .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left)
                .forEach(({ word, rect }) => {
                    let row = rows.find((item) => Math.abs(item.top - rect.top) < 2);

                    if (!row) {
                        row = { top: rect.top, words: [] };
                        rows.push(row);
                    }

                    row.words.push(word);
                });

            let wordIndex = 0;
            rows.forEach((row) => {
                row.words.forEach((word) => {
                    const delay = wordIndex * this.settings.timing.headingWordStagger;
                    word.style.setProperty("--word-delay", `${delay}ms`);
                    wordIndex += 1;
                });
            });
        });
    }
}

export default Animations;
